import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from './ui/navigation-menu';
import { Phone, Menu, X, ChevronRight, ChevronDown } from 'lucide-react';
import PDFModal from './PDFModal';
import './Header.css';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navMenuValue, setNavMenuValue] = useState('');
  const [expandedMobileSection, setExpandedMobileSection] = useState(null);
  const [pdfModal, setPdfModal] = useState(null);
  
  const whatsappNumber = '5493413047536';
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=¡Hola!`;

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setNavMenuValue('');
    setExpandedMobileSection(null);
  }, [location.pathname]);

  const toggleMobileSection = (section) => {
    setExpandedMobileSection(expandedMobileSection === section ? null : section);
  };

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const ksbSubmenu = [
    {
      title: 'Bombas',
      href: '/ksb/bombas',
      items: [
        { label: 'Catálogo de bombas', href: '/assets/ksb/KSB - Catálogo de Bombas.pdf', isPdfModal: true },
        { label: 'Aguas y efluentes', href: '/ksb/bombas/aguas-efluentes' },
        { label: 'Defensa contra inundaciones', href: '/ksb/bombas/defensa-inundaciones' },
        { label: 'Centrales térmicas', href: '/ksb/bombas/centrales-termicas' },
        { label: 'Industrias', href: '/ksb/bombas/industrias' },
        { label: 'Sistemas contra incendios', href: '/ksb/bombas/sistemas-incendios' },
        { label: 'Building', href: '/ksb/bombas/building' },
      ]
    },
    {
      title: 'Válvulas',
      href: '/ksb/valvulas',
      items: [
        { label: 'Catálogo de válvulas', href: '/assets/ksb/KSB - Catálogo Comercial de Válvulas.pdf', isPdfModal: true },
        { label: 'Válvulas', href: '/ksb/valvulas/productos' },
        { label: 'Stock de válvulas', href: '/ksb/valvulas/stock' },
      ]
    },
    {
      title: 'Otros',
      items: [
        { label: 'Sistemas de presurización', href: '/assets/ksb/KSB Hyamat - Sistemas de Presurización.pdf', isPdfModal: true },
        { label: 'Monitoreo', href: '/assets/ksb/KSB Guard - Monitoreo.pdf', isPdfModal: true },
        { label: 'Service', href: '/ksb/service' },
      ]
    }
  ];

  const tecnalSubmenu = [
    { label: 'Almacenamiento de granos', href: '/representaciones/tecnal/almacenamiento-de-granos' },
    { label: 'Aceite vegetal', href: '/representaciones/tecnal/aceite-vegetal' },
    { label: 'Alimentación animal', href: '/representaciones/tecnal/alimentacion-animal' },
    { label: 'Etanol de maíz', href: '/representaciones/tecnal/etanol-de-maiz' },
    { label: 'Movimiento portuario', href: '/representaciones/tecnal/movimiento-portuario' },
  ];

  const tecnalReferencias = [
    { label: 'Equipos rotativos', href: '/representaciones/tecnal/referencias/equipos-rotativos' },
    { label: 'Expanders', href: '/representaciones/tecnal/referencias/expanders' },
    { label: 'Enfriadoras de masa expandida', href: '/representaciones/tecnal/referencias/enfriadoras' },
  ];

  const handleNavigate = (href, isPdf = false) => {
    if (isPdf) {
      window.open(href, '_blank');
    } else {
      navigate(href);
    }
  };

  const openPdfModal = (href, title) => {
    setMobileMenuOpen(false);
    setNavMenuValue('');
    setPdfModal({ url: href, title });
  };

  return (
    <>
    <header className={`header ${isScrolled ? 'header-scrolled' : ''}`}>
      <div className="header-container">
        {/* Logo */}
        <a href="/" className="header-logo">
          <img
            src="/assets/logo-meyle-cropped.png"
            alt="Meyle Ingeniería y Servicios"
            loading="lazy"
            className="logo-img"
          />
        </a>

        {/* Desktop Navigation */}
        <nav className="header-nav desktop-nav">
          <NavigationMenu value={navMenuValue} onValueChange={setNavMenuValue}>
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuLink href="/" className="nav-link">
                  Principal
                </NavigationMenuLink>
              </NavigationMenuItem>

              <NavigationMenuItem value="representaciones">
                <NavigationMenuTrigger className="nav-trigger">Representaciones</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="submenu-wrapper">
                    <div className="submenu-container">
                      {/* KSB Submenu */}
                      <div className="submenu-section">
                        <button 
                          className="submenu-title-clickable"
                          onClick={() => handleNavigate('/ksb')}
                        >
                          KSB
                          <ChevronRight size={16} className="submenu-title-arrow" />
                        </button>
                        <div className="submenu-columns">
                          {ksbSubmenu.map((section, idx) => (
                            <div key={idx} className="submenu-column">
                              {section.href ? (
                                <button 
                                  className="submenu-category-clickable"
                                  onClick={() => handleNavigate(section.href)}
                                >
                                  {section.title}
                                  <ChevronRight size={14} className="submenu-category-arrow" />
                                </button>
                              ) : (
                                <h4 className="submenu-category">{section.title}</h4>
                              )}
                              <ul className="submenu-list">
                                {section.items.map((item, itemIdx) => (
                                  <li key={itemIdx}>
                                    <button
                                      onClick={() => item.isPdfModal
                                        ? openPdfModal(item.href, item.label)
                                        : handleNavigate(item.href, item.isPdf)
                                      }
                                      className="submenu-link"
                                    >
                                      {item.label}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* TECNAL Submenu */}
                      <div className="submenu-section">
                        <button 
                          className="submenu-title-clickable"
                          onClick={() => handleNavigate('/representaciones/tecnal')}
                        >
                          TECNAL
                          <ChevronRight size={16} className="submenu-title-arrow" />
                        </button>
                        <ul className="submenu-list">
                          {tecnalSubmenu.map((item, idx) => (
                            <li key={idx}>
                              <button 
                                onClick={() => handleNavigate(item.href)} 
                                className="submenu-link"
                              >
                                {item.label}
                              </button>
                            </li>
                          ))}
                        </ul>
                        
                        {/* Referencias en Argentina */}
                        <div className="submenu-referencias">
                          <h4 className="submenu-referencias-title">Principales referencias en el mundo</h4>
                          <ul className="submenu-list">
                            {tecnalReferencias.map((item, idx) => (
                              <li key={idx}>
                                <button 
                                  onClick={() => handleNavigate(item.href)} 
                                  className="submenu-link"
                                >
                                  {item.label}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuLink href="/sobre-nosotros" className="nav-link">
                  Sobre Nosotros
                </NavigationMenuLink>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuLink href="/clientes" className="nav-link">
                  Nuestros Clientes
                </NavigationMenuLink>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        </nav>

        {/* CTA Button */}
        <div className="header-actions">
          <Button 
            className="header-cta desktop-cta"
            onClick={() => window.open(whatsappLink, '_blank')}
          >
            <Phone size={18} className="mr-2" />
            Contactanos
          </Button>

          {/* Mobile Menu Toggle */}
          <button 
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="mobile-nav">
          <nav className="mobile-nav-content">
            <button onClick={() => { handleNavigate('/'); }} className="mobile-nav-link">Principal</button>
            <div className="mobile-nav-group">
              <button onClick={() => { handleNavigate('/ksb'); }} className="mobile-nav-group-title-clickable">KSB</button>
              
              {/* Bombas with dropdown */}
              <div className="mobile-nav-dropdown">
                <button 
                  onClick={() => toggleMobileSection('bombas')} 
                  className="mobile-nav-sublink-expandable"
                >
                  Bombas
                  <ChevronDown size={16} className={`mobile-dropdown-icon ${expandedMobileSection === 'bombas' ? 'rotated' : ''}`} />
                </button>
                {expandedMobileSection === 'bombas' && (
                  <div className="mobile-nav-nested">
                    <button onClick={() => { handleNavigate('/ksb/bombas'); }} className="mobile-nav-nested-link">Ver todo Bombas</button>
                    <button onClick={() => openPdfModal('/assets/ksb/KSB - Catálogo de Bombas.pdf', 'Catálogo de bombas')} className="mobile-nav-nested-link">Catálogo de bombas</button>
                    <button onClick={() => { handleNavigate('/ksb/bombas/aguas-efluentes'); }} className="mobile-nav-nested-link">Aguas y efluentes</button>
                    <button onClick={() => { handleNavigate('/ksb/bombas/defensa-inundaciones'); }} className="mobile-nav-nested-link">Defensa contra inundaciones</button>
                    <button onClick={() => { handleNavigate('/ksb/bombas/centrales-termicas'); }} className="mobile-nav-nested-link">Centrales térmicas</button>
                    <button onClick={() => { handleNavigate('/ksb/bombas/industrias'); }} className="mobile-nav-nested-link">Industrias</button>
                    <button onClick={() => { handleNavigate('/ksb/bombas/sistemas-incendios'); }} className="mobile-nav-nested-link">Sistemas contra incendios</button>
                    <button onClick={() => { handleNavigate('/ksb/bombas/building'); }} className="mobile-nav-nested-link">Building</button>
                  </div>
                )}
              </div>

              {/* Válvulas with dropdown */}
              <div className="mobile-nav-dropdown">
                <button 
                  onClick={() => toggleMobileSection('valvulas')} 
                  className="mobile-nav-sublink-expandable"
                >
                  Válvulas
                  <ChevronDown size={16} className={`mobile-dropdown-icon ${expandedMobileSection === 'valvulas' ? 'rotated' : ''}`} />
                </button>
                {expandedMobileSection === 'valvulas' && (
                  <div className="mobile-nav-nested">
                    <button onClick={() => { handleNavigate('/ksb/valvulas'); }} className="mobile-nav-nested-link">Ver todo Válvulas</button>
                    <button onClick={() => openPdfModal('/assets/ksb/KSB - Catálogo Comercial de Válvulas.pdf', 'Catálogo de válvulas')} className="mobile-nav-nested-link">Catálogo de válvulas</button>
                    <button onClick={() => { handleNavigate('/ksb/valvulas/productos'); }} className="mobile-nav-nested-link">Válvulas</button>
                    <button onClick={() => { handleNavigate('/ksb/valvulas/stock'); }} className="mobile-nav-nested-link">Stock de válvulas</button>
                  </div>
                )}
              </div>

              <button onClick={() => openPdfModal('/assets/ksb/KSB Hyamat - Sistemas de Presurización.pdf', 'Sistemas de presurización (Hyamat)')} className="mobile-nav-sublink">Sistemas de presurización</button>
              <button onClick={() => openPdfModal('/assets/ksb/KSB Guard - Monitoreo.pdf', 'Monitoreo (KSB Guard)')} className="mobile-nav-sublink">Monitoreo</button>
              <button onClick={() => { handleNavigate('/ksb/service'); }} className="mobile-nav-sublink">Service</button>
            </div>
            <div className="mobile-nav-group">
              <button onClick={() => { handleNavigate('/representaciones/tecnal'); }} className="mobile-nav-group-title-clickable">TECNAL</button>
              <button onClick={() => { handleNavigate('/representaciones/tecnal/almacenamiento-de-granos'); }} className="mobile-nav-sublink">Almacenamiento de granos</button>
              <button onClick={() => { handleNavigate('/representaciones/tecnal/aceite-vegetal'); }} className="mobile-nav-sublink">Aceite vegetal</button>
              <button onClick={() => { handleNavigate('/representaciones/tecnal/alimentacion-animal'); }} className="mobile-nav-sublink">Alimentación animal</button>
              <button onClick={() => { handleNavigate('/representaciones/tecnal/etanol-de-maiz'); }} className="mobile-nav-sublink">Etanol de maíz</button>
              <button onClick={() => { handleNavigate('/representaciones/tecnal/movimiento-portuario'); }} className="mobile-nav-sublink">Movimiento portuario</button>
              <p className="mobile-nav-referencias-title">Principales referencias en el mundo</p>
              <button onClick={() => { handleNavigate('/representaciones/tecnal/referencias/equipos-rotativos'); }} className="mobile-nav-sublink">Equipos rotativos</button>
              <button onClick={() => { handleNavigate('/representaciones/tecnal/referencias/expanders'); }} className="mobile-nav-sublink">Expanders</button>
              <button onClick={() => { handleNavigate('/representaciones/tecnal/referencias/enfriadoras'); }} className="mobile-nav-sublink">Enfriadoras de masa expandida</button>
            </div>
            <button onClick={() => { handleNavigate('/sobre-nosotros'); }} className="mobile-nav-link">Sobre Nosotros</button>
            <button onClick={() => { handleNavigate('/clientes'); }} className="mobile-nav-link">Nuestros Clientes</button>
            
            <Button 
              className="mobile-cta"
              onClick={() => window.open(whatsappLink, '_blank')}
            >
              <Phone size={18} className="mr-2" />
              Contactanos
            </Button>
          </nav>
        </div>
      )}
    </header>

    {pdfModal && (
      <PDFModal
        pdfUrl={pdfModal.url}
        title={pdfModal.title}
        onClose={() => setPdfModal(null)}
      />
    )}
    </>
  );
};

export default Header;